import Keyboard from "simple-keyboard";
import MicroModal from "micromodal";
import {
  getCertificateKey,
  serializeForm,
  populateForm,
  populateCertificateSelect,
  isPreviewStillValid,
  createAndRetrievePDF,
  checkFormValuesDiffer,
  updateCertificateBuyLink,
  trimRequiredFormFields
} from "./utils/certificate";
import { initInitialedForm } from "./initialed-form";

document.addEventListener("DOMContentLoaded", () => {
  let activeInput = null;
  const buyBtn =
    document.querySelector(".sp__buy-format .buy-button") ??
    document.querySelector(".form-buttons .buy-button");
  const isCertPage = !!document.querySelector(".certificate-form");

  // Initialed Form Init for Digital Certs
  initInitialedForm(buyBtn, isCertPage);

  if (!isCertPage) {
    return;
  }

  //Set Variables
  const formContainer = document.querySelector(".certificate-form");
  const form = formContainer.querySelector(".cert-form");
  const certificateSelect = formContainer.querySelector(
    ".js-certificate-number"
  );
  const deleteCertificate = formContainer.querySelector(
    ".js-delete-certificate"
  );
  const previewCertificate = formContainer.querySelector(
    ".js-preview-certificate "
  );
  const submitCertificate = formContainer.querySelector(".js-add-certificate");
  const currentISBN = certificateSelect.dataset.isbn;
  const getStored = (key) => localStorage.getItem(key);
  const setStored = (key, value) => localStorage.setItem(key, value);
  const deleteStored = (key) => localStorage.removeItem(key);
  const deactivateDOMElements = (active = true) => {
    deleteCertificate.disabled = active;
    previewCertificate.disabled = active;
  };

  const updateBuyLink = () =>
    updateCertificateBuyLink(
      buyBtn,
      getStored,
      currentISBN,
      certificateSelect,
      form
    );

  //fill certificate dropdown
  populateCertificateSelect(certificateSelect);
  updateBuyLink();

  form.querySelectorAll("input").forEach((input) => {
    input.addEventListener("blur", () => {
      const trimmed = input.value.trim();
      if (trimmed === input.value) return;

      input.value = trimmed;
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  });

  // Apply font-family on font select
  form.querySelectorAll(".form-row").forEach((row) => {
    const fontSelect = row.querySelector("select");
    const textInput = row.querySelector("input");

    if (!fontSelect || !textInput) return;

    // Apply font on change
    fontSelect.addEventListener("change", function () {
      textInput.style.fontFamily = this.value;
    });

    textInput.addEventListener("focus", () => {
      activeInput = textInput;
      keyboard.setInput(textInput.value || "");
    });
  });

  const resetFontFamily = () => {
    form.querySelectorAll("select").forEach((font) => {
      if (!font.parentElement.parentElement.querySelector("input")) {
        return;
      }

      const textInput = font.parentElement.parentElement.querySelector("input");
      textInput.style.fontFamily = font.value;
    });
  };

  form.addEventListener("input", () => {
    const currentData = serializeForm(form);
    const hasChanged = checkFormValuesDiffer(
      getStored,
      certificateSelect,
      form,
      currentData
    );

    previewCertificate.disabled = hasChanged;
    submitCertificate.disabled = !hasChanged;
    updateBuyLink();
  });

  form.addEventListener("change", () => {
    const currentData = serializeForm(form);
    const hasChanged = checkFormValuesDiffer(
      getStored,
      certificateSelect,
      form,
      currentData
    );

    previewCertificate.disabled = hasChanged;
    submitCertificate.disabled = !hasChanged;
    updateBuyLink();
  });

  //Submit Certificate Digital form data
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const submitBtn = e.submitter;

    if (submitBtn.classList.contains("js-confirm-delete")) {
      return;
    }

    trimRequiredFormFields(form);

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const data = serializeForm(form);
    const key = getCertificateKey(certificateSelect);
    const saved = getStored(getCertificateKey(certificateSelect));

    setStored(key, JSON.stringify(data));

    if (submitBtn.classList.contains("js-add-certificate")) {
      submitBtn.innerHTML = saved ? "Certificate Edited" : "Certificate Added";
      submitBtn.disabled = true;

      setTimeout(() => {
        submitBtn.innerHTML = "Update";
        submitBtn.disabled = false;
      }, 2000);
    }

    previewCertificate.disabled = checkFormValuesDiffer(
      getStored,
      certificateSelect,
      form
    );
    updateBuyLink();
    //Reset form when it's new certificate
    if (!saved) {
      populateCertificateSelect(certificateSelect);
      certificateSelect.value = certificateSelect.options.length - 1;
      deactivateDOMElements(false);
    }
  });

  // On Certificate Select, swap the form data with cookie session values
  certificateSelect.addEventListener("change", () => {
    form.reset();

    const raw = getStored(getCertificateKey(certificateSelect));
    const saved = raw ? JSON.parse(raw) : null;

    if (saved) {
      formContainer.querySelector(".js-add-certificate").innerHTML = "Update";
      populateForm(form, saved);
      deactivateDOMElements(false);
    } else {
      formContainer.querySelector(".js-add-certificate").innerHTML = "Save";
      deactivateDOMElements();
      submitCertificate.disabled = false;
    }

    resetFontFamily();

    const hasChanged = checkFormValuesDiffer(
      getStored,
      certificateSelect,
      form
    );
    previewCertificate.disabled = hasChanged;
    submitCertificate.disabled = !hasChanged;
    updateBuyLink();
  });

  function showCartMessage() {
    const messageEl = document.createElement("div");
    messageEl.className = "cart-message";
    messageEl.innerText =
      "Certificate added to cart! Click checkout to purchase your certificate(s).";

    formContainer.querySelector(".form-buttons").appendChild(messageEl);

    setTimeout(() => {
      messageEl.remove();
    }, 5000);
  }

  function openFileInModal(url, generatingPreview = false) {
    const container = document.getElementById("pdf-modal-content");

    container.innerHTML = `<img src="${url}" alt="Preview" style="width:100%;object-fit:contain;" />`;

    MicroModal.show("pdf-modal", {
      onClose: () => {
        if (generatingPreview) {
          showCartMessage();
        }
      }
    });
  }

  //Preview Certificate
  previewCertificate.addEventListener("click", async (e) => {
    let dataStored = JSON.parse(
      getStored(getCertificateKey(certificateSelect))
    );

    if (!dataStored) {
      alert("Please save your certificate first.");
      return;
    }

    if (dataStored?.preview) {
      if (isPreviewStillValid(dataStored.preview)) {
        openFileInModal(dataStored.preview);
        return;
      }
      //delete guid + preview to generate new preview
      const { preview, guid, ...resetData } = dataStored;
      dataStored = resetData;
    }

    const getPdf = await createAndRetrievePDF("preview", dataStored);
    const { guid, preview } = getPdf;
    const key = getCertificateKey(certificateSelect);

    if (guid && preview) {
      const updatedData = {
        ...dataStored,
        preview,
        guid,
        type: "preview"
      };

      setStored(key, JSON.stringify(updatedData));
      submitCertificate.disabled = true;

      //update BuyLink with new GUID (dispatches certificate-guid-updated)
      updateBuyLink();
      openFileInModal(preview, true);
    }
  });

  deleteCertificate.addEventListener("click", () => {
    const key = getCertificateKey(certificateSelect);
    const checkData = getStored(key);

    if (!checkData) return;

    MicroModal.show("delete-certificate-modal");

    const confirmBtn = document.querySelector(".js-confirm-delete");
    confirmBtn.replaceWith(confirmBtn.cloneNode(true));
    const newConfirmBtn = document.querySelector(".js-confirm-delete");

    newConfirmBtn.addEventListener("click", () => {
      const currentIndex = parseInt(key.split("_").pop(), 10);

      // Remove the selected certificate
      deleteStored(key);

      // Loop through certificates and reset keys
      let i = currentIndex + 1;
      const keyPrefix = `${currentISBN}_Certificate_`;

      while (getStored(`${keyPrefix}${i}`) !== null) {
        const data = getStored(`${keyPrefix}${i}`);
        setStored(`${keyPrefix}${i - 1}`, data);
        deleteStored(`${keyPrefix}${i}`);
        i++;
      }

      // Rebuild dropdown
      populateCertificateSelect(certificateSelect);

      // Select the previous certificate
      certificateSelect.value = `${Math.max(1, currentIndex - 1)}`;

      // Reload form
      const saved = getStored(getCertificateKey(certificateSelect));
      form.reset();

      if (!saved) {
        formContainer.querySelector(".js-add-certificate").innerHTML = "Save";
        MicroModal.close("delete-certificate-modal");
        deactivateDOMElements();
        updateBuyLink();
        return;
      }

      deactivateDOMElements(false);
      populateForm(form, JSON.parse(saved));
      MicroModal.close("delete-certificate-modal");
      updateBuyLink();
    });
  });

  //Open Close Hebrew Keyboard
  document.querySelector(".show-keyboard").addEventListener("click", (e) => {
    const showKeyboard = e.currentTarget;
    const parentElement = showKeyboard.parentElement;
    parentElement.classList.toggle("is-open");
    showKeyboard.querySelector(".copy").innerHTML =
      parentElement.classList.contains("is-open")
        ? "Hide Hebrew Keyboard"
        : "Show Hebrew Keyboard";
  });

  //Hebrew Keyboard
  const keyboard = new Keyboard({
    rtl: true,
    onKeyPress: (button) => {
      if (!activeInput) return;
      const currentValue = activeInput.value || "";
      const newValue = currentValue + button;
      activeInput.value = newValue;
      keyboard.setInput(newValue);
      activeInput.dispatchEvent(new Event("input", { bubbles: true }));
    },
    layout: {
      default: [
        "א בּ ב ג ד ה ו ז ח ט י כּ כ ך ל מ ם נ ן ס ע פּ פ ף צ ץ ק ר ש שׁ שׂ ת ׳ ״ ־ ֽ ְ ֱ ֲ ֳ ִ ֵ ֶ ַ ָ ֹ ֻ ּ וֹ וּ"
      ]
    },
    display: {
      " ": "␣"
    },
    buttonTheme: [
      {
        class: "niqqud",
        buttons: "ְ ֱ ֲ ֳ ִ ֵ ֶ ַ ָ ֹ ּ"
      }
    ]
  });
});
